import React, { useEffect, useState } from 'react';
import { FormControlLabel, Switch, InputAdornment, useTheme } from '@mui/material';
import { useForm, useWatch, Control } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Button, Input, Progress, ScrollableTabPanel } from '../../../components';
import { Contractor, ContractorStatus, ContractorType } from '../../../entities/contractor.entity';
import { ContractorForm, schema } from '../../../validations/contractor.validation';

interface ContractorPanelProps {
  contractor: Contractor;
  visible: boolean;
  inProgress: boolean;
  onClickClose: () => void;
  onSubmit: (value: Contractor) => void;
  onSend: (value: Contractor) => void;
}

const options = [
  ContractorType.Laundry,
  ContractorType.Market,
  ContractorType.Reservation,
  ContractorType.Cleaning,
  ContractorType.Furniture,
  ContractorType.Shop,
];

const statusOptions = [
  ContractorStatus.Accepted,
  ContractorStatus.Disabled,
  ContractorStatus.Pending,
];

const statusLabel: Record<ContractorStatus, string> = {
  [ContractorStatus.Accepted]: 'Aceito',
  [ContractorStatus.Disabled]: 'Desativado',
  [ContractorStatus.Pending]: 'Pendente',
};

const optionLabel: Record<ContractorType, string> = {
  [ContractorType.Laundry]: 'Lavanderia',
  [ContractorType.Market]: 'Mercado',
  [ContractorType.Reservation]: 'Reservas',
  [ContractorType.Cleaning]: 'Limpeza',
  [ContractorType.Furniture]: 'Móveis',
  [ContractorType.Shop]: 'Loja',
};

export const ContractorPanel = ({
  contractor,
  visible,
  inProgress,
  onClickClose,
  onSubmit,
  onSend,
}: ContractorPanelProps): JSX.Element => {
  const {
    register,
    control,
    reset,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContractorForm>({ mode: 'onChange', resolver: yupResolver(schema) });

  const { palette } = useTheme();
  const type = useWatch({ control, name: 'type' });
  const status = useWatch({ control, name: 'status' });

  useEffect(() => {
    reset({
      applicationFee: contractor.applicationFee
        ? new Intl.NumberFormat('pt-BR', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })
            .format(contractor.applicationFee)
            .replace(',', '.')
        : '',
      label: contractor.label ?? '',
      type: contractor.type,
      status: contractor.status,
      contactEmail: contractor.contactEmail ?? '',
    });
  }, [contractor, reset]);

  return (
    <ScrollableTabPanel
      title="Dados do parceiro"
      visible={visible}
      loading={isSubmitting}
      onClickClose={onClickClose}
      onClickSubmit={handleSubmit((values) =>
        onSubmit({
          ...contractor,
          ...values,
          status,
          applicationFee: Number(values.applicationFee.replace(',', '.')),
        })
      )}
    >
      <Input
        label="nome"
        placeholder="Digite o nome"
        validationRegister={register('label')}
        helperText={errors.label?.message || ' '}
        error={Boolean(errors.label?.message)}
      />

      <Input
        label="email de contato"
        placeholder="Digite o email de contato"
        validationRegister={register('contactEmail')}
        helperText={errors.contactEmail?.message || ' '}
        error={Boolean(errors.contactEmail?.message)}
      />

      <Autocomplete
        label="tipo"
        placeholder="Selecione o tipo"
        value={type ?? null}
        options={options}
        getOptionLabel={(opt) => optionLabel[opt]}
        onChange={(e, data) => setValue('type', data as ContractorType)}
        helperText={errors.type?.message || ' '}
        error={Boolean(errors.type?.message)}
      />

      <Input
        label="taxa"
        placeholder="00.00"
        validationRegister={register('applicationFee')}
        control={control as unknown as Control}
        startIcon={<InputAdornment position="start">%</InputAdornment>}
        helperText={errors.applicationFee?.message || ' '}
        error={Boolean(errors.applicationFee?.message)}
      />

      <Autocomplete
        label="Status"
        placeholder="Selecione o status"
        value={status ?? ContractorStatus.Pending}
        options={statusOptions}
        getOptionLabel={(opt) => statusLabel[opt]}
        onChange={(e, data) => setValue('status', data as ContractorStatus)}
        helperText={errors.type?.message || ''}
        error={Boolean(errors.type?.message)}
        getOptionDisabled={(option) => option === ContractorStatus.Pending}
      />

      {inProgress ? (
        <>
          <Button variant="text" color="primary" disabled>
            Enviando...
          </Button>
          <Progress />
        </>
      ) : (
        <>
          <Button variant="text" color="primary" onClick={() => onSend(contractor)}>
            Reenviar email
          </Button>
        </>
      )}
    </ScrollableTabPanel>
  );
};

export default ContractorPanel;
